import { type MRT_Row, type MRT_TableOptions } from 'material-react-table'
import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { FarmSubdivision, FormDefinition, MetricEvent } from '../../types/apiModels'
import { formModes } from '../../utils/constants'
import { isReportedByQueryLayer } from '../../utils/Errors/isReportedByQueryLayer'
import { useCustomSnackbarError } from '../../utils/Snackbar/useCustomSnackbarError'
import {
  ColumnOption,
  createColumns,
  FormMode,
  MetricEventRow,
  processRecordData,
  sortRowsByPaddocks,
} from '../ExpandableDataTable/dataTableHelpers'
import { ExpandableDataTable } from '../ExpandableDataTable/ExpandableDataTable'
import { createTableActions } from './TableActions'

type DataTableProps = {
  metricEvents: MetricEvent[]
  formDefinition: FormDefinition
  farmSubdivision: FarmSubdivision
  formMode: FormMode
  handleDelete: (selected: MRT_Row<MetricEventRow>[]) => void
  handleMetricReset: () => void
  handleSelect: (selected: MRT_Row<MetricEventRow>[]) => void
}

export const DataTable: FC<DataTableProps> = ({
  farmSubdivision,
  formDefinition,
  formMode,
  handleDelete,
  handleMetricReset,
  handleSelect,
  metricEvents,
}) => {
  const [tableData, setTableData] = useState<{
    columns: ColumnOption[] | null
    rows: MetricEventRow[]
  }>({
    columns: null,
    rows: [],
  })
  const [globalFilter, setGlobalFilter] = useState('')
  const { handleError } = useCustomSnackbarError()

  const customOptions: Partial<MRT_TableOptions<MetricEventRow>> = useMemo(
    () => ({
      enableBatchRowSelection: false,
      enableFullScreenToggle: true,
      enableGlobalFilter: true,
      enableRowNumbers: true,
      enableRowSelection: formMode !== formModes.Disable,
      enableSelectAll: false,
      enableTopToolbar: true,
      getRowId: row => String(row.id),
      positionToolbarAlertBanner: 'none',
      rowNumberDisplayMode: 'original',
      state: {
        globalFilter,
      },
      onGlobalFilterChange: setGlobalFilter,
      renderTopToolbarCustomActions: ({ table }): ReactNode =>
        createTableActions({
          table,
          formMode,
          handleSelect,
          handleDelete,
          title: formDefinition.label,
        }),
    }),
    [formDefinition.label, formMode, handleDelete, handleSelect, globalFilter],
  )

  const columnNames = useMemo(
    () => [
      'isDraft',
      'metricYear',
      'paddocks',
      'isVerified',
      'findings',
      'notApplicable',
      'metricStatusId',
    ],
    [],
  )

  useEffect(() => {
    if (!formDefinition || !farmSubdivision) {
      setTableData({ columns: null, rows: [] })
      return (): void => {}
    }

    let isMounted = true

    const processData = async (): Promise<void> => {
      try {
        const [newColumns, newRows] = await Promise.all([
          createColumns(formDefinition, columnNames, [
            { name: 'paddocks', options: { sort: true } },
          ]),
          Promise.all(
            metricEvents.map(record =>
              processRecordData({
                record,
                formDefinition,
                farmSubdivision,
                formMode,
                handleMetricReset,
              }),
            ),
          ) || [],
        ])

        if (isMounted) {
          setTableData({ columns: newColumns, rows: sortRowsByPaddocks(newRows) })
        }
      } catch (error) {
        // Every API read behind a row is a refData lookup through ensureQueryData.
        if (error instanceof Error && isMounted && !isReportedByQueryLayer(error)) {
          handleError(error)
        }
      }
    }

    processData()

    return (): void => {
      isMounted = false
    }
  }, [
    formDefinition,
    metricEvents,
    farmSubdivision,
    formMode,
    handleMetricReset,
    handleError,
    columnNames,
  ])

  const { columns, rows } = tableData

  if (!columns || !rows) return null

  return (
    <ExpandableDataTable
      columns={columns}
      customOptions={customOptions}
      data={rows}
      formDefinition={formDefinition}
    />
  )
}
